import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const field = "h-12 rounded-xl bg-white border-[#0B281E]/15 text-[#1A2621] placeholder:text-[#52635B]/70 focus-visible:ring-[#C88A35]";
export const card = "rounded-[28px] bg-[#FAF8F5] text-[#1A2621] p-6 sm:p-10 lg:p-12 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)]";

export const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block font-mono-d text-[11px] uppercase tracking-[0.18em] text-[#52635B] mb-2">{children}</label>
);

export const Segmented = ({ value, onChange, options, testid }) => (
  <div className="grid grid-flow-col auto-cols-fr gap-1 rounded-xl bg-white border border-[#0B281E]/15 p-1 h-12" data-testid={testid}>
    {options.map(([v, l]) => (
      <button type="button" key={v} onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`${testid}-${v}`}
        className={`relative rounded-lg text-sm transition-colors ${value === v ? "text-[#FAF8F5]" : "text-[#1A2621] hover:text-[#A8701F]"}`}>
        {value === v && <motion.span layoutId={`seg-${testid}`} className="absolute inset-0 rounded-lg bg-[#0B281E]" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
        <span className="relative">{l}</span>
      </button>
    ))}
  </div>
);

export const useSubmit = (path) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (payload) => {
    setLoading(true);
    try {
      await axios.post(`${API}${path}`, payload);
      setDone(true);
      toast.success("Request received — I'll be in touch soon.");
      return true;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check your details and try again." : d || "Something went wrong. Please call or email instead.");
      return false;
    } finally {
      setLoading(false);
    }
  };
  return { loading, done, setDone, submit };
};

export const SubmitRow = ({ loading, label, testid }) => (
  <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
    <p className="flex items-center gap-2 text-xs text-[#52635B]"><ShieldCheck className="w-4 h-4 text-[#C88A35]" /> Your info stays private. No spam, ever.</p>
    <button type="submit" disabled={loading} data-testid={testid} className="inline-flex justify-center items-center gap-2 rounded-full bg-[#0B281E] text-[#FAF8F5] px-8 py-4 font-semibold hover:bg-[#C88A35] hover:text-[#071510] transition-colors disabled:opacity-60">
      {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? "Sending…" : label}
    </button>
  </div>
);

export const Success = ({ onReset, testid }) => (
  <div data-testid={testid} className="rounded-[28px] bg-[#FAF8F5] text-[#0B281E] p-10 lg:p-14 min-h-[520px] flex flex-col justify-center">
    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="w-16 h-16 rounded-full bg-[#0B281E] text-[#E5A855] grid place-items-center"><Check className="w-8 h-8" /></motion.span>
    <h3 className="font-serif-d text-4xl lg:text-5xl mt-8">Thank you. You're in good hands.</h3>
    <p className="mt-4 text-[#52635B] max-w-md leading-relaxed">Your request is in. I'll review your details and reach out within one business day with options tailored to you.</p>
    <button onClick={onReset} data-testid={`${testid}-reset-btn`} className="mt-8 self-start text-sm font-semibold underline underline-offset-8">Submit another request</button>
  </div>
);
